/**
 * 3D neural-network visualization using vanilla three.js.
 *
 * Renders each architecture block as a type-specific grid of units and draws
 * translucent links between consecutive blocks. The camera
 * auto-rotates and supports click-drag to orbit.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export interface Layer3DSpec {
  label: string;
  units: number;
  color: string;
  kind?: string;
  detail?: string;
  /** Optional per-neuron labels (aligned with units, up to 64 shown). */
  neuronLabels?: string[];
  /** Optional per-neuron numeric value (e.g., feature importance, weight). */
  neuronValues?: number[];
  /** Format hint for neuronValues: 'percent' | 'number' | 'prob'. */
  valueFormat?: 'percent' | 'number' | 'prob';
  /** Unit suffix for value display (e.g., 'importance'). */
  valueUnit?: string;
}

interface Props {
  layers: Layer3DSpec[];
  height?: number;
}

// Clamp very large layers so the scene stays readable.
const MAX_NEURONS_PER_LAYER = 64;

type LayerVisualRole =
  | 'input'
  | 'attention'
  | 'feed_forward'
  | 'recurrent'
  | 'convolution'
  | 'dense'
  | 'tree_ensemble'
  | 'ensemble'
  | 'aggregation'
  | 'latent'
  | 'output'
  | 'model';

const LAYER_TYPE_META: Record<LayerVisualRole, {
  label: string;
  color: string;
  shape: 'cube' | 'sphere' | 'octahedron' | 'box' | 'cylinder' | 'dodecahedron' | 'cone' | 'icosahedron' | 'tetrahedron';
  summary: string;
  lineColor: number;
}> = {
  input: {
    label: 'Input',
    color: '#38bdf8',
    shape: 'cube',
    summary: 'feature tensor',
    lineColor: 0x38bdf8,
  },
  attention: {
    label: 'Attention',
    color: '#8b5cf6',
    shape: 'octahedron',
    summary: 'context mixing',
    lineColor: 0xa78bfa,
  },
  feed_forward: {
    label: 'Feed Forward',
    color: '#f59e0b',
    shape: 'box',
    summary: 'token-wise transform',
    lineColor: 0xfbbf24,
  },
  recurrent: {
    label: 'Recurrent',
    color: '#22c55e',
    shape: 'cylinder',
    summary: 'sequence memory',
    lineColor: 0x4ade80,
  },
  convolution: {
    label: 'Convolution',
    color: '#06b6d4',
    shape: 'dodecahedron',
    summary: 'local pattern filters',
    lineColor: 0x22d3ee,
  },
  dense: {
    label: 'Dense',
    color: '#84cc16',
    shape: 'sphere',
    summary: 'fully connected',
    lineColor: 0xa3e635,
  },
  tree_ensemble: {
    label: 'Tree Ensemble',
    color: '#10b981',
    shape: 'cone',
    summary: 'estimator forest',
    lineColor: 0x34d399,
  },
  ensemble: {
    label: 'Ensemble',
    color: '#ec4899',
    shape: 'icosahedron',
    summary: 'model collection',
    lineColor: 0xf472b6,
  },
  aggregation: {
    label: 'Aggregation',
    color: '#f97316',
    shape: 'tetrahedron',
    summary: 'vote / score merge',
    lineColor: 0xfb923c,
  },
  latent: {
    label: 'Latent',
    color: '#6366f1',
    shape: 'octahedron',
    summary: 'compressed state',
    lineColor: 0x818cf8,
  },
  output: {
    label: 'Output',
    color: '#14b8a6',
    shape: 'tetrahedron',
    summary: 'prediction head',
    lineColor: 0x2dd4bf,
  },
  model: {
    label: 'Model',
    color: '#64748b',
    shape: 'sphere',
    summary: 'model block',
    lineColor: 0x94a3b8,
  },
};

const normalizeLayerRole = (layer: Layer3DSpec): LayerVisualRole => {
  const raw = `${layer.kind || ''} ${layer.label || ''}`.toLowerCase().replace(/[-\s]+/g, '_');
  if (raw.includes('input')) return 'input';
  if (raw.includes('attention') || raw.includes('transformer')) return 'attention';
  if (raw.includes('feed_forward') || raw.includes('ffn')) return 'feed_forward';
  if (raw.includes('lstm') || raw.includes('gru') || raw.includes('recurrent')) return 'recurrent';
  if (raw.includes('conv') || raw.includes('cnn') || raw.includes('tcn') || raw.includes('wavenet')) return 'convolution';
  if (raw.includes('tree') || raw.includes('forest') || raw.includes('boost')) return 'tree_ensemble';
  if (raw.includes('ensemble') || raw.includes('stack')) return 'ensemble';
  if (raw.includes('aggregation') || raw.includes('vote')) return 'aggregation';
  if (raw.includes('latent') || raw.includes('encoder') || raw.includes('vae') || raw.includes('autoencoder')) return 'latent';
  if (raw.includes('output') || raw.includes('prediction')) return 'output';
  if (raw.includes('dense') || raw.includes('linear') || raw.includes('mlp')) return 'dense';
  return 'model';
};

const isComputeRole = (role: LayerVisualRole) => role !== 'input';

const escapeHtml = (value: unknown) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatValue = (
  value: number,
  format?: Layer3DSpec['valueFormat'],
) => {
  if (format === 'percent') return `${(value * 100).toFixed(2)}%`;
  if (format === 'prob') return value.toFixed(4);
  return Math.abs(value) < 1 ? value.toFixed(4) : value.toFixed(3);
};

const geometryForRole = (role: LayerVisualRole, radius: number): THREE.BufferGeometry => {
  const shape = LAYER_TYPE_META[role].shape;
  switch (shape) {
    case 'cube':
      return new THREE.BoxGeometry(radius * 1.65, radius * 1.65, radius * 1.65);
    case 'octahedron':
      return new THREE.OctahedronGeometry(radius * 1.35, 1);
    case 'box':
      return new THREE.BoxGeometry(radius * 2.1, radius * 1.25, radius * 1.25);
    case 'cylinder':
      return new THREE.CylinderGeometry(radius * 0.72, radius * 0.72, radius * 1.9, 18);
    case 'dodecahedron':
      return new THREE.DodecahedronGeometry(radius * 1.25, 0);
    case 'cone':
      return new THREE.ConeGeometry(radius * 1.15, radius * 2, 5);
    case 'icosahedron':
      return new THREE.IcosahedronGeometry(radius * 1.25, 0);
    case 'tetrahedron':
      return new THREE.TetrahedronGeometry(radius * 1.45, 0);
    case 'sphere':
    default:
      return new THREE.SphereGeometry(radius, 18, 18);
  }
};

const backingGeometryForRole = (
  role: LayerVisualRole,
  width: number,
  height: number,
): THREE.BufferGeometry => {
  if (role === 'attention' || role === 'latent' || role === 'ensemble') {
    return new THREE.CircleGeometry(Math.max(width, height) * 0.52, 48);
  }
  if (role === 'output' || role === 'aggregation') {
    const shape = new THREE.Shape();
    shape.moveTo(0, height * 0.52);
    shape.lineTo(width * 0.52, 0);
    shape.lineTo(0, -height * 0.52);
    shape.lineTo(-width * 0.52, 0);
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }
  return new THREE.PlaneGeometry(width, height);
};

const gridFor = (units: number): { cols: number; rows: number } => {
  const n = Math.min(units, MAX_NEURONS_PER_LAYER);
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  return { cols, rows };
};

const NeuralNetwork3D: React.FC<Props> = ({ layers, height = 380 }) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const rotatingRef = useRef(false);
  const [rotating, setRotating] = useState(false);
  const resetSignalRef = useRef(0);
  const [resetKey, setResetKey] = useState(0);
  const [pinned, setPinned] = useState<null | {
    layer: Layer3DSpec;
    layerIndex: number;
    neuronIndex: number;
    totalUnits: number;
  }>(null);

  // Stringify layer spec so effect only re-runs when layers change structurally.
  const layersKey = useMemo(
    () =>
      layers
        .map((l) => [
          l.label,
          l.units,
          l.color,
          l.kind || '',
          l.detail || '',
          l.valueFormat || '',
          l.valueUnit || '',
          (l.neuronLabels || []).slice(0, MAX_NEURONS_PER_LAYER).join(','),
          (l.neuronValues || []).slice(0, MAX_NEURONS_PER_LAYER).join(','),
        ].join(':'))
        .join('|'),
    [layers],
  );

  const architectureSummary = useMemo(() => {
    const roleEntries = layers.map((layer) => ({
      role: normalizeLayerRole(layer),
    }));
    const roleCounts = roleEntries.reduce<Record<string, number>>((acc, entry) => {
      acc[entry.role] = (acc[entry.role] || 0) + 1;
      return acc;
    }, {});
    const visibleUnits = layers.reduce((sum, layer) => sum + Math.min(layer.units, MAX_NEURONS_PER_LAYER), 0);
    const totalUnits = layers.reduce((sum, layer) => sum + layer.units, 0);
    const computeBlocks = roleEntries.filter((entry) => isComputeRole(entry.role)).length;
    const typeChips = Object.entries(roleCounts).map(([role, count]) => ({
      role: role as LayerVisualRole,
      count,
      meta: LAYER_TYPE_META[role as LayerVisualRole],
    }));
    return {
      computeBlocks,
      visibleUnits,
      totalUnits,
      typeChips,
    };
  }, [layers]);

  useEffect(() => {
    rotatingRef.current = rotating;
  }, [rotating]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const h = height;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1020);

    const camera = new THREE.PerspectiveCamera(45, width / h, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, h);
    mount.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.65);
    dirLight.position.set(5, 10, 7.5);
    scene.add(dirLight);

    // Build neuron positions per layer.
    const layerSpacing = 6;
    const cellSpacing = 1.3;
    const neuronRadius = 0.35;

    const layerPositions: THREE.Vector3[][] = [];
    const group = new THREE.Group();
    // Track each instanced mesh so raycasting can map a hit back to layer+neuron.
    const neuronMeshes: {
      mesh: THREE.InstancedMesh;
      layerIndex: number;
      totalUnits: number;
      baseColor: THREE.Color;
    }[] = [];

    layers.forEach((layer, idx) => {
      const role = normalizeLayerRole(layer);
      const meta = LAYER_TYPE_META[role];
      const { cols, rows } = gridFor(layer.units);
      const count = Math.min(layer.units, MAX_NEURONS_PER_LAYER);
      const offsetX = -((cols - 1) * cellSpacing) / 2;
      const offsetY = -((rows - 1) * cellSpacing) / 2;
      const z = (idx - (layers.length - 1) / 2) * layerSpacing;

      const positions: THREE.Vector3[] = [];
      const color = new THREE.Color(meta.color || layer.color);

      // Instanced units (faster for many repeated layer-type shapes).
      const unitGeo = geometryForRole(role, neuronRadius);
      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color.clone().multiplyScalar(role === 'attention' || role === 'output' ? 0.42 : 0.24),
        roughness: role === 'feed_forward' || role === 'tree_ensemble' ? 0.32 : 0.46,
        metalness: role === 'attention' || role === 'latent' ? 0.32 : 0.14,
      });
      const mesh = new THREE.InstancedMesh(unitGeo, material, count);

      const dummy = new THREE.Object3D();
      let i = 0;
      for (let r = 0; r < rows && i < count; r++) {
        for (let c = 0; c < cols && i < count; c++) {
          const x = offsetX + c * cellSpacing;
          const y = offsetY + r * cellSpacing;
          dummy.position.set(x, y, z);
          dummy.updateMatrix();
          mesh.setMatrixAt(i, dummy.matrix);
          positions.push(new THREE.Vector3(x, y, z));
          i++;
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
      // Initialize per-instance color so hover highlighting via setColorAt works.
      for (let j = 0; j < count; j++) {
        mesh.setColorAt(j, color);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.userData = { layerIndex: idx };
      group.add(mesh);
      neuronMeshes.push({
        mesh,
        layerIndex: idx,
        totalUnits: layer.units,
        baseColor: color.clone(),
      });

      // Layer label plane (transparent disc behind neurons).
      const panelWidth = cols * cellSpacing + 2;
      const panelHeight = rows * cellSpacing + 2;
      const planeGeo = backingGeometryForRole(role, panelWidth, panelHeight);
      const planeMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: role === 'attention' ? 0.075 : 0.045,
        side: THREE.DoubleSide,
      });
      const plane = new THREE.Mesh(planeGeo, planeMat);
      plane.position.set(0, 0, z);
      group.add(plane);

      // Type outline. Attention/latent blocks get circular halos; dense/feed-forward
      // blocks get rectangular frames, so shape still reads when zoomed out.
      const ringRadius = Math.max(cols, rows) * cellSpacing * 0.58;
      const ringGeo = role === 'feed_forward' || role === 'dense' || role === 'input'
        ? new THREE.EdgesGeometry(new THREE.BoxGeometry(panelWidth, panelHeight, 0.04))
        : new THREE.RingGeometry(
          ringRadius,
          ringRadius + 0.025,
          role === 'output' || role === 'aggregation' ? 4 : 64,
        );
      const ringMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: role === 'attention' ? 0.52 : 0.38,
        side: THREE.DoubleSide,
      });
      const ring = role === 'feed_forward' || role === 'dense' || role === 'input'
        ? new THREE.LineSegments(ringGeo, ringMat)
        : new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(0, 0, z - 0.01);
      group.add(ring);

      layerPositions.push(positions);
    });

    // Draw connection lines between consecutive layers (sampled if huge).
    for (let i = 0; i < layerPositions.length - 1; i++) {
      const from = layerPositions[i];
      const to = layerPositions[i + 1];
      if (!from.length || !to.length) continue;

      // Limit total lines per gap to keep rendering smooth.
      const maxLines = 350;
      const total = from.length * to.length;
      const step = Math.max(1, Math.ceil(total / maxLines));

      const positions: number[] = [];
      let idx = 0;
      for (let a = 0; a < from.length; a++) {
        for (let b = 0; b < to.length; b++) {
          if (idx % step === 0) {
            const p1 = from[a];
            const p2 = to[b];
            positions.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
          }
          idx++;
        }
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3),
      );
      const mat = new THREE.LineBasicMaterial({
        color: LAYER_TYPE_META[normalizeLayerRole(layers[i + 1])].lineColor,
        transparent: true,
        opacity: normalizeLayerRole(layers[i + 1]) === 'attention' ? 0.16 : 0.11,
      });
      const lines = new THREE.LineSegments(geom, mat);
      group.add(lines);
    }

    scene.add(group);

    // Frame the network.
    const totalDepth = (layers.length - 1) * layerSpacing;
    const maxDim = Math.max(
      ...layers.map((l) => Math.max(gridFor(l.units).cols, gridFor(l.units).rows)),
      4,
    );
    const frameRadius = Math.max(totalDepth * 0.7, maxDim * cellSpacing);
    camera.position.set(frameRadius * 1.2, frameRadius * 0.55, frameRadius * 1.4);
    camera.lookAt(0, 0, 0);

    // Simple orbit controls (mouse drag + wheel) — no extra dep.
    let isDragging = false;
    let dragMoved = false;
    let prevX = 0;
    let prevY = 0;
    let yaw = Math.atan2(camera.position.x, camera.position.z);
    let pitch = Math.asin(
      camera.position.y /
        Math.sqrt(
          camera.position.x ** 2 + camera.position.y ** 2 + camera.position.z ** 2,
        ),
    );
    let radius = camera.position.length();

    const updateCamera = () => {
      pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, pitch));
      camera.position.x = radius * Math.cos(pitch) * Math.sin(yaw);
      camera.position.y = radius * Math.sin(pitch);
      camera.position.z = radius * Math.cos(pitch) * Math.cos(yaw);
      camera.lookAt(0, 0, 0);
    };

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      dragMoved = false;
      prevX = e.clientX;
      prevY = e.clientY;
    };
    const onMouseUp = () => {
      isDragging = false;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        if (Math.abs(dx) + Math.abs(dy) > 2) dragMoved = true;
        prevX = e.clientX;
        prevY = e.clientY;
        yaw -= dx * 0.01;
        pitch += dy * 0.01;
        updateCamera();
        return;
      }
      // Hover raycasting
      handleHover(e);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      radius = Math.max(4, Math.min(200, radius + e.deltaY * 0.05));
      updateCamera();
    };

    // Raycasting state
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let hoveredNeuron: {
      mesh: THREE.InstancedMesh;
      layerIndex: number;
      neuronIndex: number;
      baseColor: THREE.Color;
    } | null = null;

    const restoreHovered = () => {
      if (!hoveredNeuron) return;
      const { mesh, neuronIndex, baseColor } = hoveredNeuron;
      mesh.setColorAt(neuronIndex, baseColor);
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      hoveredNeuron = null;
    };

    const highlightColor = new THREE.Color('#ffffff');

    const hideTooltip = () => {
      const tip = tooltipRef.current;
      if (tip) tip.style.display = 'none';
    };

    const showTooltip = (
      x: number,
      y: number,
      layer: Layer3DSpec,
      layerIndex: number,
      neuronIndex: number,
      totalUnits: number,
    ) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      const shown = Math.min(totalUnits, MAX_NEURONS_PER_LAYER);
      const hiddenNote =
        totalUnits > MAX_NEURONS_PER_LAYER
          ? `<div style="opacity:.65;margin-top:2px">showing ${shown} of ${totalUnits} units</div>`
          : '';
      const role = normalizeLayerRole(layer);
      const meta = LAYER_TYPE_META[role];
      const previousLayer = layers[layerIndex - 1];
      const nextLayer = layers[layerIndex + 1];
      const detail = layer.detail
        ? `<div style="opacity:.75;margin-top:2px">${escapeHtml(layer.detail)}</div>`
          : '';
      const kind = `<div style="opacity:.65;font-size:10px;text-transform:uppercase;letter-spacing:.5px">${escapeHtml(meta.label)} block${layer.kind ? ` - ${escapeHtml(layer.kind)}` : ''}</div>`;
      const ioLine = `
        <div style="opacity:.75;margin-top:3px">
          ${previousLayer ? `from ${escapeHtml(previousLayer.label)} (${previousLayer.units})` : 'network input'}
          ${nextLayer ? ` -> ${escapeHtml(nextLayer.label)} (${nextLayer.units})` : ' -> network output'}
        </div>
      `;
      // Per-neuron label + value
      const neuronLabel = layer.neuronLabels?.[neuronIndex];
      const neuronValue = layer.neuronValues?.[neuronIndex];
      let valueStr = '';
      if (neuronValue != null && Number.isFinite(neuronValue)) {
        valueStr = formatValue(neuronValue, layer.valueFormat);
      }
      const neuronLine = neuronLabel
        ? `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.12)">
             <div style="font-weight:600">${escapeHtml(neuronLabel)}</div>
             ${valueStr ? `<div style="opacity:.85">${layer.valueUnit ? `${escapeHtml(layer.valueUnit)}: ` : ''}${escapeHtml(valueStr)}</div>` : ''}
           </div>`
        : valueStr
          ? `<div style="margin-top:4px;opacity:.85">${layer.valueUnit ? `${escapeHtml(layer.valueUnit)}: ` : 'Value: '}${escapeHtml(valueStr)}</div>`
          : '';
      tip.innerHTML = `
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">
          <span style="display:inline-block;width:10px;height:10px;border-radius:${role === 'feed_forward' ? '2px' : '50%'};background:${meta.color}"></span>
          <b>${escapeHtml(layer.label)}</b>
        </div>
        ${kind}
        <div>${escapeHtml(meta.summary)}</div>
        <div>Unit #${neuronIndex + 1} / ${totalUnits}</div>
        <div style="opacity:.75">Block ${layerIndex + 1} of ${layers.length}</div>
        ${ioLine}
        ${detail}
        ${neuronLine}
        ${hiddenNote}
      `;
      tip.style.display = 'block';
      // Position relative to mount container.
      const rect = renderer.domElement.getBoundingClientRect();
      const localX = x - rect.left;
      const localY = y - rect.top;
      const tipRect = tip.getBoundingClientRect();
      const maxX = rect.width - tipRect.width - 8;
      const maxY = rect.height - tipRect.height - 8;
      tip.style.left = `${Math.max(4, Math.min(localX + 12, maxX))}px`;
      tip.style.top = `${Math.max(4, Math.min(localY + 12, maxY))}px`;
    };

    const handleHover = (e: MouseEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const meshes = neuronMeshes.map((n) => n.mesh);
      const hits = raycaster.intersectObjects(meshes, false);
      if (!hits.length || hits[0].instanceId == null) {
        if (hoveredNeuron) {
          restoreHovered();
          hideTooltip();
        }
        renderer.domElement.style.cursor = isDragging ? 'grabbing' : 'grab';
        return;
      }
      const hit = hits[0];
      const entry = neuronMeshes.find((n) => n.mesh === hit.object);
      if (!entry) return;
      const neuronIndex = hit.instanceId!;
      const sameAsBefore =
        hoveredNeuron &&
        hoveredNeuron.mesh === entry.mesh &&
        hoveredNeuron.neuronIndex === neuronIndex;
      if (!sameAsBefore) {
        restoreHovered();
        entry.mesh.setColorAt(neuronIndex, highlightColor);
        if (entry.mesh.instanceColor) entry.mesh.instanceColor.needsUpdate = true;
        hoveredNeuron = {
          mesh: entry.mesh,
          layerIndex: entry.layerIndex,
          neuronIndex,
          baseColor: entry.baseColor,
        };
      }
      renderer.domElement.style.cursor = 'pointer';
      showTooltip(
        e.clientX,
        e.clientY,
        layers[entry.layerIndex],
        entry.layerIndex,
        neuronIndex,
        entry.totalUnits,
      );
    };

    const onMouseLeave = () => {
      if (hoveredNeuron) restoreHovered();
      hideTooltip();
    };

    const onClick = (_e: MouseEvent) => {
      if (dragMoved) return; // don't treat drag-release as click
      if (hoveredNeuron) {
        const h = hoveredNeuron;
        setPinned({
          layer: layers[h.layerIndex],
          layerIndex: h.layerIndex,
          neuronIndex: h.neuronIndex,
          totalUnits: neuronMeshes.find((n) => n.mesh === h.mesh)?.totalUnits || 0,
        });
      } else {
        setPinned(null);
      }
    };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    renderer.domElement.addEventListener('mouseleave', onMouseLeave);
    renderer.domElement.addEventListener('click', onClick);

    // Animate.
    let frameId = 0;
    let previousFrameTime = performance.now();
    const initialYaw = yaw;
    let signalCount = resetSignalRef.current;

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const frameTime = performance.now();
      const delta = Math.min((frameTime - previousFrameTime) / 1000, 0.1);
      previousFrameTime = frameTime;
      if (!isDragging && rotatingRef.current) {
        yaw += delta * 0.3;
        updateCamera();
      }
      // Handle reset.
      if (resetSignalRef.current !== signalCount) {
        signalCount = resetSignalRef.current;
        yaw = initialYaw;
        pitch = 0.35;
        radius = frameRadius * 1.8;
        updateCamera();
      }
      renderer.render(scene, camera);
    };
    animate();

    // Resize handling.
    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(mount);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('mouseleave', onMouseLeave);
      renderer.domElement.removeEventListener('click', onClick);
      scene.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: any) => m.dispose?.());
          } else {
            obj.material.dispose?.();
          }
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, height, resetKey]);

  const pinnedRole = pinned ? normalizeLayerRole(pinned.layer) : null;
  const pinnedMeta = pinnedRole ? LAYER_TYPE_META[pinnedRole] : null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, position: 'relative' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          3D Network
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={rotating ? 'Pause rotation' : 'Resume rotation'}>
          <IconButton size="small" onClick={() => setRotating((r) => !r)}>
            {rotating ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view">
          <IconButton
            size="small"
            onClick={() => {
              resetSignalRef.current += 1;
              setResetKey((k) => k + 1);
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Chip
          size="small"
          label={`${layers.length} blocks`}
          variant="outlined"
          sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
        />
        <Chip
          size="small"
          label={`${architectureSummary.computeBlocks} compute`}
          variant="outlined"
          sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
        />
        <Chip
          size="small"
          label={`${architectureSummary.visibleUnits}/${architectureSummary.totalUnits} units shown`}
          variant="outlined"
          sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
        />
        {architectureSummary.typeChips.map(({ role, count, meta }) => (
          <Chip
            key={role}
            size="small"
            label={`${meta.label} ${count}`}
            variant="outlined"
            sx={{
              height: 22,
              borderColor: `${meta.color}66`,
              color: meta.color,
              '& .MuiChip-label': { px: 0.75, fontSize: 11 },
            }}
          />
        ))}
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          ref={mountRef}
          sx={{
            width: '100%',
            height: '100%',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        />
        {/* Hover tooltip (positioned via JS inside the canvas effect) */}
        <Box
          ref={tooltipRef}
          sx={{
            position: 'absolute',
            display: 'none',
            pointerEvents: 'none',
            bgcolor: 'rgba(20,24,40,0.92)',
            color: 'common.white',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: 12,
            lineHeight: 1.35,
            maxWidth: 260,
            zIndex: 2,
            boxShadow: 3,
          }}
        />
        {pinned && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              left: 8,
              bottom: 8,
              p: 1,
              maxWidth: 280,
              bgcolor: 'rgba(20,24,40,0.95)',
              color: 'common.white',
              border: '1px solid rgba(255,255,255,0.15)',
              zIndex: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: pinnedRole === 'feed_forward' ? 0.5 : '50%',
                  bgcolor: pinnedMeta?.color || pinned.layer.color,
                  transform: pinnedRole === 'output' || pinnedRole === 'aggregation' ? 'rotate(45deg)' : 'none',
                }}
              />
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                {pinned.layer.label}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                onClick={() => setPinned(null)}
                sx={{ color: 'inherit', opacity: 0.7 }}
              >
                <Typography variant="caption">x</Typography>
              </IconButton>
            </Stack>
            <Typography
              variant="caption"
              sx={{ display: 'block', opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {pinnedMeta?.label || pinned.layer.kind || 'Model'} block{pinned.layer.kind ? ` - ${pinned.layer.kind}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Unit #{pinned.neuronIndex + 1} / {pinned.totalUnits}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
              Block {pinned.layerIndex + 1} of {layers.length}
            </Typography>
            {pinnedMeta?.summary && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                {pinnedMeta.summary}
              </Typography>
            )}
            {pinned.layer.detail && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                {pinned.layer.detail}
              </Typography>
            )}
            {pinned.layer.neuronLabels?.[pinned.neuronIndex] && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                {pinned.layer.neuronLabels[pinned.neuronIndex]}
              </Typography>
            )}
            {pinned.layer.neuronValues?.[pinned.neuronIndex] != null && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                {pinned.layer.valueUnit ? `${pinned.layer.valueUnit}: ` : 'Value: '}
                {formatValue(
                  pinned.layer.neuronValues[pinned.neuronIndex],
                  pinned.layer.valueFormat,
                )}
              </Typography>
            )}
          </Paper>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1 }}
      >
        {layers.map((l, i) => {
          const role = normalizeLayerRole(l);
          const meta = LAYER_TYPE_META[role];
          return (
            <Tooltip
              key={`${l.label}-${i}`}
              title={`${meta.label} block - ${meta.summary}${l.detail ? ` - ${l.detail}` : ''}`}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  fontSize: 11,
                  maxWidth: 260,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.25,
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: role === 'feed_forward' || role === 'input' ? 0.5 : '50%',
                    bgcolor: meta.color,
                    transform: role === 'output' || role === 'aggregation' ? 'rotate(45deg)' : 'none',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {l.label} ({l.units}) - {meta.label}
                </Typography>
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.5 }}
      >
        Hover a unit for details - click to pin - drag to orbit - scroll to zoom - up to 64 units are rendered per block
      </Typography>
    </Paper>
  );
};

export default NeuralNetwork3D;
